/**
 * PurchaseButton — the "Get started" CTA on the public /n2/plans page.
 *
 * Behavior is fully gated by `REACT_APP_PUBLIC_PURCHASE_ENABLED`.
 * When the env flag is OFF (the default), the component renders as an
 * inert "Coming soon" affordance so no visitor is ever misled into
 * thinking they can complete a purchase for a plan whose Stripe Price
 * IDs are not yet configured. When the flag is ON, the button:
 *
 *   • Signed-out click: stashes the plan+billing intent in
 *     sessionStorage, then navigates to /login with the same intent
 *     also in the query string (belt + suspenders). The auth screens
 *     read it on success and redirect the user straight to
 *     /upgrade?plan=…&billing=….
 *   • Signed-in click: navigates directly to /upgrade with the plan
 *     and billing preselected.
 *
 * The button never initiates the Stripe checkout call itself — that's
 * still Upgrade.tsx's job. This keeps the "displayed plan matches the
 * charge" invariant enforced by a single code path.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { palette, type as typeTokens } from './theme';
import type { PlanKey } from './plansCatalog';
import {
  isPublicPurchaseEnabled,
  saveIntent,
  checkoutContinueUrl,
  type IntentBilling,
} from './purchaseIntent';

// Public plan keys (as displayed on /n2/plans) → productConfig product
// keys the backend understands. Boring Bucket is deliberately absent
// — it's application-gated, not part of this flow.
const PLAN_TO_PRODUCT: Partial<Record<PlanKey, string>> = {
  premier: 'premier',
  'spx-only': 'spx',
  'es-only': 'es',
  'index-etf-research': 'index-etf-research',
  'futures-options-research': 'futures-options-research',
  'all-research': 'all-research',
};

export function planToProductKey(plan: PlanKey): string | null {
  return PLAN_TO_PRODUCT[plan] || null;
}

interface Props {
  planKey: PlanKey;
  billing: IntentBilling;
  /** Rendered when the feature flag is off. Keep this benign — a link
   *  to /n2/plans or a "coming soon" span, never anything that looks
   *  clickable-to-purchase. */
  disabledFallback: React.ReactNode;
}

export const PurchaseButton: React.FC<Props> = ({ planKey, billing, disabledFallback }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  if (!isPublicPurchaseEnabled()) return <>{disabledFallback}</>;

  const productKey = planToProductKey(planKey);
  // If we can't map the plan to a product (e.g. Boring Bucket), fall
  // back to the disabled affordance — never render a checkout button
  // that has no destination.
  if (!productKey) return <>{disabledFallback}</>;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const target = checkoutContinueUrl({ planKey: productKey, billing });
    if (isAuthenticated) {
      navigate(target);
      return;
    }
    // Signed-out: preserve the intent through the auth funnel. The
    // sessionStorage record is a defensive fallback for flows that
    // drop the query string; the primary carrier is `?redirect=`,
    // which Login / Register / VerifyEmail already thread through
    // via safeRedirectPath.
    saveIntent({ planKey: productKey, billing });
    navigate(`/login?redirect=${encodeURIComponent(target)}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '10px 18px',
        marginTop: 4,
        borderRadius: 6,
        border: `1px solid ${palette.ink}`,
        background: palette.ink,
        color: palette.paper,
        fontFamily: typeTokens.sans,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      {isAuthenticated ? 'Continue to checkout →' : 'Sign in to subscribe →'}
    </button>
  );
};

export default PurchaseButton;
